import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import Clarity from '@microsoft/clarity';
import { environment } from '../../../environments/environment';
import type { User } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private initialized = false;

  init(): void {
    if (!this.canUseClarity() || this.initialized) {
      return;
    }

    Clarity.init(environment.clarity.projectId as string);
    this.initialized = true;
    this.setSessionTags();
  }

  identifyUser(user: User): void {
    if (!this.canTrack()) {
      return;
    }

    Clarity.identify(user.id, undefined, undefined, user.email || undefined);
  }

  setUserTags(user: User): void {
    if (!this.canTrack()) {
      return;
    }

    Clarity.setTag('role', user.role);
    Clarity.setTag('preferred_language', user.preferredLanguage ?? 'unknown');
    Clarity.setTag('account_active', String(user.isActive));
    Clarity.setTag('email_verified', String(user.emailVerified));
  }

  setSessionTags(): void {
    if (!this.canTrack()) {
      return;
    }

    Clarity.setTag('platform', Capacitor.isNativePlatform() ? 'native' : 'web');
    Clarity.setTag('auth_state', 'anonymous');
  }

  markLoggedOut(): void {
    if (!this.canTrack()) {
      return;
    }

    Clarity.setTag('auth_state', 'logged_out');
  }

  markLoggedIn(): void {
    if (!this.canTrack()) {
      return;
    }

    Clarity.setTag('auth_state', 'logged_in');
  }

  trackEvent(eventName: string): void {
    if (!this.canTrack()) {
      return;
    }

    Clarity.event(eventName);
  }

  private canUseClarity(): boolean {
    return Boolean(environment.production && environment.clarity.projectId && typeof window !== 'undefined');
  }

  private canTrack(): boolean {
    if (!this.initialized) {
      this.init();
    }
    return this.initialized && this.canUseClarity();
  }
}
